import React, { Fragment, useState } from 'react';
import { BtnPrimary } from '../components/common/Buttons';
import Modal from '../components/common/Modal';
import toast from 'react-hot-toast';
import { IBrand, IClient } from '../types/brand';
import {
  fetchAgentList,
  fetchBrandList,
  fetchClientList,
} from '../hooks/queries';
import TableWithSearchCTA from '../components/TableWithSearchCTA';
import AddClient from '../components/forms/AddClient';
import AddBrand from '../components/forms/AddBrand';
import ClientInfo from '../components/ClientInfo';
import { formatClientData } from '../utils';
import { useClientColumns } from '../components/columns/useClientCol';

const Client: React.FC = () => {
  const {
    data: brandsData,
    isLoading: isBrandsLoading,
    isFetching: isBrandsFetching,
    error: brandsError,
    refetch: refetchBrands,
  } = fetchBrandList();
  const {
    data: clientsData,
    isLoading: isClientsLoading,
    isFetching: isClientsFetching,
    refetch: refetchClients,
  } = fetchClientList();
  const { data: agentsData, isLoading: isAgentsLoading } = fetchAgentList();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [isClientInfoModalOpen, setIsClientInfoModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);

  const rowData = formatClientData(brandsData?.brands || []);
  const isLoading = isBrandsLoading || isClientsLoading || isAgentsLoading;
  const isFetching = isBrandsFetching || isClientsFetching;
  const error = brandsError;

  const validClient = (clientId: string) =>
    clientsData?.clients.find((c) => c._id === clientId) || null;

  const refetchAll = () => {
    refetchClients();
    refetchBrands();
  };

  const onModalClose = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
  };

  const handleBrandModalOpen = (client: IClient | null) => {
    setSelectedClient(client);
    setSelectedBrand(null);
    setIsAddBrandModalOpen(true);
  };

  const handleClientInfoModalClose = () => {
    setIsClientInfoModalOpen(false);
    setSelectedClient(null);
  };

  const handleBrandEdit = (brand: IBrand) => {
    setSelectedBrand(brand);
    setIsAddBrandModalOpen(true);
  };

  const columns = useClientColumns({
    onView: (client) => {
      setSelectedClient(client);
      setIsClientInfoModalOpen(true);
    },
    onEdit: (client) => {
      setSelectedClient(client);
      setIsModalOpen(true);
    },
    onAddBrand: handleBrandModalOpen,
    onEditBrand: handleBrandEdit,
    onEditClient: (client) => {
      setSelectedClient(client);
      setIsModalOpen(true);
    },
    validClient,
    setSelectedClient,
    setIsClientInfoModalOpen,
    handleBrandModalOpen,
  });

  return (
    <div className="w-full" style={{ height: 'calc(100vh - 3rem)' }}>
      <TableWithSearchCTA
        rowData={rowData}
        columnDefs={columns}
        ctas={{
          custom: (
            <Fragment>
              <BtnPrimary onClick={() => setIsModalOpen(true)}>
                + Add client
              </BtnPrimary>
            </Fragment>
          ),
        }}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error as Error}
        refetch={refetchAll}
        headerTitle="Clients"
        description="Manage and organize clients"
        enableRefresh
        storageKey="brands_table"
      />

      <Modal
        isOpen={isModalOpen}
        onRequestClose={onModalClose}
        title={selectedClient ? 'Edit Client' : 'Onboard Client'}
        className="!w-[40%] 2xl:!w-[25%]"
      >
        <AddClient
          initialData={selectedClient || undefined}
          isEditing={!!selectedClient}
          onSuccess={() => {
            toast.success(
              `Client ${selectedClient ? 'updated' : 'onboarded'} successfully`
            );
            refetchAll();
            onModalClose();
          }}
          onError={(e) => toast.error(e.message)}
        />
      </Modal>

      <Modal
        isOpen={isAddBrandModalOpen}
        onRequestClose={() => {
          setIsAddBrandModalOpen(false);
          if (!selectedBrand) setSelectedClient(null);
        }}
        title={selectedBrand ? 'Edit Brand' : 'Add Brand'}
        className="!w-[40%] 3xl:!w-[20%]"
        height="fit-content"
      >
        <AddBrand
          initialData={selectedClient || undefined}
          selectedBrand={selectedBrand!}
          isEditing={!!selectedBrand}
          agentsData={agentsData?.agents || []}
          onSuccess={() => {
            toast.success(`Brand added successfully`);
            setIsAddBrandModalOpen(false);
          }}
          onError={(e) => toast.error(e.message)}
        />
      </Modal>

      {isClientInfoModalOpen && selectedClient && (
        <div
          className="fixed inset-0 z-50 flex justify-end backdrop-blur-sm bg-black/30"
          onClick={handleClientInfoModalClose}
        >
          <div
            className="bg-greyTen w-full max-w-[60%] h-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ClientInfo
              agentsData={agentsData?.agents || []}
              rowData={rowData}
              client={selectedClient}
              onClose={handleClientInfoModalClose}
              handleBrandEdit={handleBrandEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Client;
